<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createSmbConfig } from "./fs";
  import { makeFileSystemSmb, testSmbConnection } from "./fs-smb";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createSmbConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.host.trim() != "" && !testingConnection);

  function smbWho(): string {
    let host = config.host.includes(":") ? `[${config.host}]` : config.host;
    if (config.port != "" && config.port != "445") {
      host += ":" + config.port;
    }
    if (config.share != "") {
      host += "/" + config.share;
    }
    if (config.user == "") {
      return host;
    }
    return `${config.user}@${host}`;
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing smb connection");
    try {
      config.host = config.host.trim();
      config.port = config.port.trim();
      config.user = config.user.trim();
      config.domain = config.domain.trim();
      config.share = config.share.trim();
      config.name = config.name.trim();
      await testSmbConnection(config);
      if (config.name == "") {
        config.name = smbWho();
      }
      let fs = makeFileSystemSmb(config);
      let who = config.name;
      telegramNotify(`New SMB connection for ${who}`);
      captureEvent("smb-login", { host: config.host });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect SMB">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-smb-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-smb-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <div class="mt-3 flex gap-3">
        <div class="flex grow flex-col">
          <label for="input-smb-host">Host:</label>
          <input
            type="text"
            id="input-smb-host"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            bind:value={config.host}
          />
        </div>
        <div class="flex w-20 flex-col">
          <label for="input-smb-port">Port:</label>
          <input
            type="text"
            id="input-smb-port"
            autocomplete="off"
            class="outline-hidden border px-2 py-1"
            placeholder="445"
            bind:value={config.port}
          />
        </div>
      </div>

      <label class="mt-3" for="input-smb-share">Share: <span class="text-gray-400">(empty for all shares)</span></label>
      <input
        type="text"
        id="input-smb-share"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.share}
      />

      <label class="mt-3" for="input-smb-user">User: <span class="text-gray-400">(empty for guest)</span></label>
      <input
        type="text"
        id="input-smb-user"
        autocomplete="username"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-smb-password">Password:</label>
      <input
        type="password"
        id="input-smb-password"
        autocomplete="current-password"
        class="outline-hidden border px-2 py-1"
        bind:value={config.password}
      />

      <label class="mt-3" for="input-smb-domain">Domain: <span class="text-gray-400">(optional)</span></label>
      <input
        type="text"
        id="input-smb-domain"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.domain}
      />

      <div class="mt-3 w-72 text-gray-500">
        Sumatra File Manager Online's server connects to the host, so it must be able to reach it: a host on your home
        network will not work.
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect SMB</button>
    </div>
  {/snippet}
</DialogBase>
